// React Router v6 configuration.
// All routes share the Shell layout (header + nav + mobile-nav).
// Route modules are lazy-loaded. Options and portfolio are still placeholders
// pending Week 6; everything else is fully ported.

import { lazy, Suspense, type ReactNode } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import Shell from './components/Shell'
import ErrorBoundary from './components/ErrorBoundary'

// Wrap each lazy route in an ErrorBoundary so a render-time crash in one
// section doesn't blank the whole shell. Suspense fallback kept per-route.
function Guarded({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<Loading />}>{children}</Suspense>
    </ErrorBoundary>
  )
}

const Overview    = lazy(() => import('./routes/Overview'))
const Projects    = lazy(() => import('./routes/Projects'))
const Estimates   = lazy(() => import('./routes/Estimates'))
const Expenses    = lazy(() => import('./routes/Expenses'))
const Payments    = lazy(() => import('./routes/Payments'))
const Subs        = lazy(() => import('./routes/Subs'))
const Vendors     = lazy(() => import('./routes/Vendors'))
const Clients     = lazy(() => import('./routes/Clients'))
const Schedule    = lazy(() => import('./routes/Schedule'))
const MyHours     = lazy(() => import('./routes/MyHours'))
const Reports     = lazy(() => import('./routes/Reports'))
const BidRoom     = lazy(() => import('./routes/BidRoom'))
const Portfolio   = lazy(() => import('./routes/Portfolio'))
const Options     = lazy(() => import('./routes/Options'))

function Loading() {
  return (
    <div style={{ padding: '2rem', display: 'flex', justifyContent: 'center' }}>
      <div className="skeleton" style={{ width: 200, height: 20, borderRadius: 6 }} />
    </div>
  )
}

export default function Router() {
  return (
    <BrowserRouter>
      <Routes>
        <Route element={<Shell />}>
          <Route index element={<Guarded><Overview /></Guarded>} />
          <Route path="projects"  element={<Guarded><Projects /></Guarded>} />
          <Route path="estimates" element={<Guarded><Estimates /></Guarded>} />
          <Route path="expenses"  element={<Guarded><Expenses /></Guarded>} />
          <Route path="payments"  element={<Guarded><Payments /></Guarded>} />
          <Route path="clients"   element={<Guarded><Clients /></Guarded>} />
          <Route path="subs"      element={<Guarded><Subs /></Guarded>} />
          <Route path="vendors"   element={<Guarded><Vendors /></Guarded>} />
          <Route path="bidroom"   element={<Guarded><BidRoom /></Guarded>} />
          <Route path="portfolio" element={<Guarded><Portfolio /></Guarded>} />
          <Route path="reports"   element={<Guarded><Reports /></Guarded>} />
          <Route path="timelog"   element={<Guarded><MyHours /></Guarded>} />
          <Route path="schedule"  element={<Guarded><Schedule /></Guarded>} />
          <Route path="options"   element={<Guarded><Options /></Guarded>} />
          {/* Catch-all → overview */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
